import React from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { UserPlus } from '@styled-icons/fa-solid/UserPlus';
import { SignIn } from '@styled-icons/fa-solid/SignIn';
import { device } from '../../constants/device-sizes';
import { getSiteName } from '../../hooks/useSiteConfig';
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernSubtitle
} from './shared/ModalBase';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  color: var(--clr-sec);
  opacity: 0.7;
  z-index: 10;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
    transform: scale(1.1);
  }
`;


const Description = styled.p`
  color: var(--clr-sec);
  font-size: 1.1rem;
  text-align: center;
  margin: 1.5rem 0 2rem 0;
  line-height: 1.6;
  opacity: 0.9;

  @media ${device.tablet} {
    font-size: 1.2rem;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin: 2rem 0;
`;

const OptionCard = styled.div`
  background: var(--clr-prim);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent, rgba(102, 126, 234, 0.05));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: var(--clr-purple);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const OptionIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: var(--clr-purple);
  
  svg {
    width: 24px;
    height: 24px;
    filter: drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3));
  }
`;

const OptionTitle = styled.h3`
  color: var(--clr-sec);
  font-size: 1.1rem;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  
  @media ${device.tablet} {
    font-size: 1.2rem;
  }
`;

const OptionDescription = styled.p`
  color: var(--clr-sec);
  font-size: 0.85rem;
  margin: 0;
  opacity: 0.7;
  line-height: 1.4;
  
  @media ${device.tablet} {
    font-size: 0.9rem;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  text-align: center;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }

  span {
    color: var(--clr-sec);
    font-size: 0.9rem;
    font-weight: 600;
    opacity: 0.6;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: var(--clr-sec);
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SignInOrUpModal = ({
  visible,
  onClose,
  onGoogleSignIn,
  onCreateAccount,
  onSignIn,
}) => {
  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: { 
          zIndex: 1000, 
          backgroundColor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
        },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose} />

          <ModernTitle>Sign In or Create Account</ModernTitle>
          
          <ModernSubtitle>
            Choose how you'd like to access your {getSiteName()} account
          </ModernSubtitle>

          <OptionsContainer>
            <OptionCard onClick={onGoogleSignIn}>
              <OptionIcon>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </OptionIcon>
              <OptionTitle>Sign In with Google</OptionTitle>
              <OptionDescription>
                Easier to use, less anonymous
              </OptionDescription>
            </OptionCard>

            <OrDivider>
              <span>Or</span>
            </OrDivider>

            <OptionCard onClick={onCreateAccount}>
              <OptionIcon>
                <UserPlus />
              </OptionIcon>
              <OptionTitle>Create Anonymous Account Number</OptionTitle>
              <OptionDescription>
                Generates a 20-digit number to sign in with. Harder to use, more anonymous
              </OptionDescription>
            </OptionCard>

            <OptionCard onClick={onSignIn}>
              <OptionIcon>
                <SignIn />
              </OptionIcon>
              <OptionTitle>Sign In with Account Number</OptionTitle>
              <OptionDescription>
                Enter your 20-digit account number to access your existing account and data
              </OptionDescription>
            </OptionCard>
          </OptionsContainer>

          <ButtonContainer>
            <CancelButton onClick={onClose}>
              Cancel
            </CancelButton>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default SignInOrUpModal;